import { useEffect, useRef, useState } from 'react';
import { useUserStore } from '../../contexts/userStore';
import EventsService from '../../services/EventsService';
import List from '../../components/List/List';
import { TimeAgo } from '../../components/TimeAgo/TimeAgo';
import './PresenceMonitoring.css';
import { useSSEContext } from '../../contexts/sse';
import ReceptionPhoto from '../../components/ReceptionPhoto/ReceptionPhoto';
import { formatDate } from '../../utils/date';
import Button from '../../components/Button/Button';
import { TbDoorExit } from 'react-icons/tb';
import Pagination from '../../components/Pagination/Pagination';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DatetimeWithRelative } from '../../components/DatetimeWithRelative/DatetimeWithRelative';

const PresenceMonitoring = () => {
   const { selectedCondominio } = useUserStore();
   const [collumnClicked, setCollumnClicked] = useState({
      collumnTitle: "createdAt",
      orderDirection: "DESC",
   });
   const [currentPage, setCurrentPage] = useState(1);
   const [maxPage, setMaxPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(8);
   const [total, setTotal] = useState(0);
   const [presentUsers, setPresentUsers] = useState([]);
   const { sseReceived } = useSSEContext();
   const [isLoading, setIsLoading] = useState(true);
   const cancelTokenRef = useRef(null);
   const { t } = useTranslation();

   async function loadPresentUsers() {
      if (cancelTokenRef.current) {
         cancelTokenRef.current.abort();
      }
      cancelTokenRef.current = new AbortController();
      setIsLoading(true);
      const response = await EventsService.getPresentUsers(
         selectedCondominio,
         currentPage,
         rowsPerPage,
         cancelTokenRef.current.signal
      );
      setIsLoading(false);
      if (response?.data) {
         setPresentUsers(response.data.results);
         setMaxPage(Math.ceil(response?.data?.total / rowsPerPage));
         setTotal(response?.data?.total);
      }
   }

   async function handleExit(eventId) {
      const response = await EventsService.finishAccessEvent(eventId, selectedCondominio);
   }

   useEffect(() => {
      loadPresentUsers();
   }, [
      selectedCondominio,
      currentPage,
      rowsPerPage
   ]);

   useEffect(() => {
      if (sseReceived?.action === "createAccessEvent") {
         loadPresentUsers();
      }
   }, [sseReceived]);

   return (
      <div className='list-presents'>
         <List
            isLoading={isLoading}
            selectableRows={false}
            handleCollumnTitleClick={(collumnIndex) =>
               setCollumnClicked(collumnIndex)
            }
            collumsObjIndex={[
               {
                  collumnLabel: t("common.user"), index: "userData.name", type: "custom", custom: ({ row }) => {
                     return (
                        <div className="reception-person-list">
                           <div className="reception-image-list">
                              <ReceptionPhoto
                                 timeToZoom={400}
                                 urlLogo={row.userData?.signed_url_photo?.replace("[:STORAGE_ADDRESS:]", window.location.hostname)}
                              />
                           </div>
                           <div className="flex flex-col gap-1.5">
                              <Link to={`/device-users/${row.userData?.uuid}`} className="hover:opacity-65 duration-200">
                                 <span>{row.userData?.name ?? row.user ?? 'Desconhecido'}</span>
                              </Link>
                              {row?.profile && <span className="muted-sm my-0! px-0!">{row.profile}</span>}
                              {/* {Math.random() > 0.5 ? (
                                 <span className='inline-flex flex-row items-center gap-1.5'>
                                    <span className='size-1.5 rounded-full bg-amber-500'></span>
                                    <span className='text-amber-500 text-[11px]!'>Expirado</span>
                                 </span>
                              ) : (
                                 <span className='inline-flex flex-row items-center gap-1.5'>
                                    <span className='size-1.5 rounded-full bg-green-500'></span>
                                    <span className='text-green-500 text-[11px]!'>Liberado</span>
                                 </span>
                              )} */}
                           </div>
                        </div>
                     );
                  }
               },
               {
                  collumnLabel: t("common.contact"), type: "custom", custom: ({ row }) => {
                     const contact = row.userData?.phone_number && row.userData?.phone_number.trim() !== "" ? row.userData?.phone_number : row.userData?.mail;
                     if (!contact) {
                        return <span className="opacity-40">{t("common.noContact")}</span>
                     }
                     return <a href={`${contact?.includes('@') ? 'mailto' : 'tel'}:${contact}`} className='hover:text-(--primaryColor) duration-200!'>{contact}</a>
                  }
               },
               {
                  collumnLabel: t("common.unit"), index: "userData.unit_name", custom: ({ row }) => {
                     if (row.userData?.unit_name) {
                        return row.userData?.unit_name;
                     }
                     return <span className="text-xs! opacity-50!">{t("common.doesNotContain")}</span>;
                  }
               },
               {
                  collumnLabel: t("common.entry"), type: "custom", custom: ({ row }) => {
                     return (
                        <DatetimeWithRelative datetime={row.entry_date} />
                     );
                  }
               },
               {
                  collumnLabel: t("common.expiration"), type: "custom", custom: ({ row }) => {
                     if (row.expire_at) {
                        const expireAt = new Date(row.expire_at);
                        if (expireAt > new Date()) {
                           return <span className='px-3! py-1.5! rounded-full border! border-yellow-500 text-yellow-500'>{t("common.expireAt")} <TimeAgo date={row.expire_at} simple={true} /></span>
                        } else {
                           return (
                              <span className='px-3! py-1.5! rounded-full border! border-red-400 text-red-400'>
                                 {expireAt < new Date() ? t("common.expiredAt") : t("common.expired")} <TimeAgo date={row.expire_at} simple={true} />
                              </span>
                           );
                        }
                     }
                     return <span className='px-3! py-1.5! rounded-full border! border-green-500 text-green-500'>{t("common.freeAccess")}</span>;
                  }
               },
               { collumnLabel: t("common.currentLocation"), index: "last_destiny", type: "text" },
               {
                  collumnLabel: t("common.actions"), type: "actions", custom: ({ row }) => {
                     return (
                        <div className='flex justify-end flex-row w-full'>
                           <Button
                              className="px-2.5! [&>.button-container]:gap-0!"
                              icon={<TbDoorExit size={20} />}
                              onClick={() => handleExit(row.eventId)}
                           />
                        </div>
                     );
                  }
               }
            ]}
            data={presentUsers}
         />
         <Pagination
            rowsPerPage={rowsPerPage}
            maxPage={maxPage}
            total={total}
            changePage={(page) =>
               setCurrentPage(page)
            }
            changeRowsPerPage={(rowsPerPage) =>
               setRowsPerPage(rowsPerPage)
            }
         />
      </div>
   );
};

export default PresenceMonitoring;
